import { FloatingFormItem, InputDecimal } from '@/components/_common';
import {
  InvoiceCstCsosnCodes,
  optionsCsosnCodes,
  optionsCstCodes,
} from '@/core/domain/invoice/invoiceNoteItems/invoiceNoteItemsParts/InvoiceNoteItems.const';
import {
  IcmsBaseCalculationDeterminationMode,
  IcmsExemptionReason,
  IcmsOrigin,
  IcmsStDeterminationMode,
  InvoiceItemFormValues,
  TaxRegimeEnum,
} from '@/core/domain/invoice/invoiceNoteItems/invoiceNoteItemsParts/InvoiceNoteItems.types';
import { useDefaultEmptyFieldError } from '@/hooks';
import { Card, Col, Form, FormInstance, Row, Select } from 'antd';
import { useCallback } from 'react';

interface NoteItemsIcmsTabsFormProps {
  form: FormInstance<InvoiceItemFormValues>;
  changeInvoiceItemData: (
    ncmId: number,
    taxRegime: TaxRegimeEnum,
    changeTaxRegime?: boolean,
  ) => Promise<void>;
}

export default function NoteItemsIcmsTabsForm({
  form,
  changeInvoiceItemData,
}: NoteItemsIcmsTabsFormProps) {
  const defaultEmptyFieldError = useDefaultEmptyFieldError();
  const watchTaxRegime = Form.useWatch(['itemTaxes', 'taxRegime'], form);
  const watchCstIcms = Form.useWatch(['itemTaxes', 'icmsCst'], form);
  const ncmId = Form.useWatch('ncmId', form);

  const taxRegimeOptions = [
    { value: TaxRegimeEnum.normal, label: 'Regime Normal' },
    { value: TaxRegimeEnum.simple, label: 'Simples Nacional' },
    {
      value: TaxRegimeEnum.simpleNationalExcessSublimitGrossRevenue,
      label: 'Simples nacional com excesso do sublimite',
    },
  ];

  const originOptions = [
    {
      value: IcmsOrigin.NATIONAL_EXCEPT_CODES_3458,
      label: '0 - Nacional, exceto as indicadas nos códigos 3, 4, 5 e 8',
    },
    {
      value: IcmsOrigin.FOREIGN_DIRECT_IMPORT_EXCEPT_CODE_6,
      label:
        '1 - Estrangeira - Importação direta, exceto a indicada no código 6',
    },
    {
      value: IcmsOrigin.FOREIGN_ACQUIRED_IN_DOMESTIC_MARKET_EXCEPT_CODE_7,
      label:
        '2 - Estrangeira - Adquirida no mercado interno, exceto a indicada no código 7',
    },
    {
      value: IcmsOrigin.NATIONAL_CONTENT_IMPORTATION_40_70,
      label:
        '3 - Nacional, mercadoria ou bem com Conteúdo de Importação superior a 40% e inferior a 70%',
    },
    {
      value: IcmsOrigin.NATIONAL_BASIC_PRODUCTION_PROCESS,
      label:
        '4 - Nacional, cuja produção tenha sido feita em conformidade com os processos produtivos básicos de que tratam as legislações citadas nos Ajustes',
    },
    {
      value: IcmsOrigin.NATIONAL_CONTENT_IMPORTATION_40,
      label:
        '5 - Nacional, mercadoria ou bem com Conteúdo de Importação inferior ou a 40%',
    },
    {
      value: IcmsOrigin.FOREIGN_DIRECT_IMPORT_NO_NATIONAL_SIMILAR_CAMEX,
      label:
        '6 - Estrangeira - Importação direta, sem similar nacional, constante em lista de CAMEX e gás natural',
    },
    {
      value:
        IcmsOrigin.FOREIGN_ACQUIRED_IN_DOMESTIC_MARKET_NO_NATIONAL_SIMILAR_CAMEX,
      label:
        '7 - Estrangeira - Adquirida no mercado interno, sem similar nacional, constante lista CAMEX e gás natural',
    },
    {
      value: IcmsOrigin.NATIONAL_CONTENT_IMPORTATION_70,
      label: '8 - Nacional, mercadoria',
    },
  ];

  const icmsModalityOptions = [
    {
      value: IcmsBaseCalculationDeterminationMode.MARGIN_VALUE_ADDED,
      label: '0 - Margem Valor Agregado (%)',
    },
    {
      value: IcmsBaseCalculationDeterminationMode.TABLE_PRICE_MAX,
      label: '1 - Pauta (valor)',
    },
    {
      value: IcmsBaseCalculationDeterminationMode.LIST_PRICE_MAX,
      label: '2 - Preço Tabelado Máx. (valor)',
    },
    {
      value: IcmsBaseCalculationDeterminationMode.OPERATION_VALUE,
      label: '3 - Valor da operação',
    },
  ];

  const icmsStModalityOptions = [
    {
      value: IcmsStDeterminationMode.TABLE_PRICE_MAX,
      label: '0 - Preço tabelado ou máximo sugerido',
    },
    {
      value: IcmsStDeterminationMode.NEGATIVE_LIST,
      label: '1 - Lista Negativa (valor)',
    },
    {
      value: IcmsStDeterminationMode.POSITIVE_LIST,
      label: '2 - Lista Positiva (valor)',
    },
    {
      value: IcmsStDeterminationMode.NEUTRAL_LIST,
      label: '3 - Lista Neutra (valor)',
    },
    {
      value: IcmsStDeterminationMode.MARGIN_VALUE_ADDED,
      label: '4 - Margem Valor Agregado (%)',
    },
    { value: IcmsStDeterminationMode.TABLE_PRICE, label: '5 - Pauta (valor)' },
    {
      value: IcmsStDeterminationMode.OPERATION_VALUE,
      label: '6 - Valor da Operação (NT 2019.001)',
    },
  ];

  const icmsExemptionReasonOptions = [
    {
      value: IcmsExemptionReason.TAXI,
      label: '1 - Táxi',
    },
    {
      value: IcmsExemptionReason.PRODUCER,
      label: '3 - Uso na agropecuária',
    },
    {
      value: IcmsExemptionReason.FLEET_RENTAL,
      label: '4 - Frotista/Locadora',
    },
    {
      value: IcmsExemptionReason.DIPLOMATIC_CONSULAR,
      label: '5 - Diplomático/Consular',
    },
    {
      value: IcmsExemptionReason.AMAZON_MOTORCYCLES,
      label:
        '6 - Utilitários e Motocicletas da Amazônia Ocidental e Áreas de Livre Comércio (Resolução 714/88 e 790/94 – CONTRAN e suas alterações)',
    },
    {
      value: IcmsExemptionReason.SUFRAMA,
      label: '7 - SUFRAMA',
    },
    {
      value: IcmsExemptionReason.SALE_TO_PUBLIC_AGENCY,
      label: '8 - Venda a Órgão Público',
    },
    {
      value: IcmsExemptionReason.OTHERS,
      label: '9 - Outros. (NT 2011/004)',
    },
    {
      value: IcmsExemptionReason.DISABLED_DRIVER,
      label: '10 - Deficiente Condutor (Convênio ICMS 38/12)',
    },
    {
      value: IcmsExemptionReason.DISABLED_NON_DRIVER,
      label: '11 - Deficiente Não Condutor (Convênio ICMS 8/12)',
    },
    {
      value: IcmsExemptionReason.OLYMPICS_2016,
      label: '16 - Olimpíadas Rio 2016 (NT 2015.002)',
    },
    {
      value: IcmsExemptionReason.REQUESTED_BY_TAX_AUTHORITY,
      label: '90 - Solicitado pelo Fisco (NT2016.002)',
    },
  ];

  const visibleField = useCallback(
    (rules: string[]) => {
      if (rules) {
        return rules.includes(InvoiceCstCsosnCodes[watchCstIcms]);
      }
    },
    [watchCstIcms],
  );

  return (
    <Form
      form={form}
      onValuesChange={async (field) => {
        if (field?.itemTaxes?.taxRegime && ncmId) {
          form.resetFields([['itemTaxes', 'icmsCst']]);
          await changeInvoiceItemData(
            ncmId,
            field?.itemTaxes?.taxRegime,
            false,
          );
        }
      }}
    >
      <Card title="ICMS" type="inner" style={{ marginBottom: 24 }}>
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={24} md={12} lg={6}>
            <FloatingFormItem
              name={['itemTaxes', 'taxRegime']}
              label="Regime tributário"
              rules={[defaultEmptyFieldError]}
              getValueFromEvent={(value: string) => {
                form.setFieldValue(['itemTaxes', 'icmsCst'], undefined);
                return value;
              }}
            >
              <Select options={taxRegimeOptions} allowClear />
            </FloatingFormItem>
          </Col>

          <Col xs={24} sm={24} md={12} lg={6}>
            <FloatingFormItem
              name={['itemTaxes', 'icmsCst']}
              label={watchTaxRegime !== TaxRegimeEnum.simple ? 'CST' : 'CSOSN'}
              rules={[defaultEmptyFieldError]}
            >
              <Select
                options={(watchTaxRegime !== TaxRegimeEnum.simple
                  ? optionsCstCodes
                  : optionsCsosnCodes
                )?.map((code) => ({
                  value: code.id,
                  label: code.code,
                }))}
              />
            </FloatingFormItem>
          </Col>

          <Col xs={24} sm={24} md={24} lg={12}>
            <FloatingFormItem
              name={['itemTaxes', 'icmsOrigin']}
              label="Origem"
              rules={[defaultEmptyFieldError]}
            >
              <Select options={originOptions} />
            </FloatingFormItem>
          </Col>

          {visibleField(['101', '201']) && (
            <Col xs={24} sm={24} md={24} lg={12}>
              <FloatingFormItem
                name={['itemTaxes', 'icmsSimpleNationalCreditPercentage']}
                label="Alíquota aplicável de cálculo do crédito"
              >
                <InputDecimal fractions={4} max={100} />
              </FloatingFormItem>
            </Col>
          )}

          {visibleField(['101', '201']) && (
            <Col xs={24} sm={24} md={24} lg={12}>
              <FloatingFormItem
                name={['itemTaxes', 'icmsSimpleNationalCreditValue']}
                label="Valor crédito"
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>
          )}

          {visibleField(['10', '00', '20', '51', '70', '90', '900']) && (
            <Col xs={24} sm={24} md={24} lg={12}>
              <FloatingFormItem
                name={['itemTaxes', 'icmsAliquot']}
                label="Alíquota do imposto"
              >
                <InputDecimal fractions={4} max={100} />
              </FloatingFormItem>
            </Col>
          )}

          {visibleField(['10', '00', '20', '51', '70', '90', '900']) && (
            <Col xs={24} sm={24} md={24} lg={12}>
              <FloatingFormItem
                name={['itemTaxes', 'icmsBaseCalculationDeterminationMode']}
                label="Modalidade de determinação da BC"
              >
                <Select allowClear options={icmsModalityOptions} />
              </FloatingFormItem>
            </Col>
          )}

          {visibleField(['10', '00', '20', '51', '70', '90', '900']) && (
            <Col xs={24} sm={24} md={24} lg={12}>
              <FloatingFormItem
                name={['itemTaxes', 'icmsBaseCalculationValue']}
                label="Valor da base de cálculo"
                initialValue={0}
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>
          )}

          {visibleField(['20', '51', '70', '90', '900']) && (
            <Col xs={24} sm={24} md={24} lg={12}>
              <FloatingFormItem
                name={['itemTaxes', 'icmsBaseCalculationReductionPercentage']}
                label="Percentual da redução da BC"
              >
                <InputDecimal max={100} />
              </FloatingFormItem>
            </Col>
          )}

          {visibleField(['10', '00', '51', '70', '90', '500', '900']) && (
            <Col xs={24} sm={24} md={24} lg={12}>
              <FloatingFormItem
                name={['itemTaxes', 'icmsValue']}
                label="Valor do ICMS"
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>
          )}
        </Row>
      </Card>

      {visibleField([
        '00',
        '10',
        '30',
        '41',
        '60',
        '70',
        '90',
        '201',
        '202',
        '203',
        '500',
        '900',
      ]) && (
        <Card title="ICMS ST" type="inner" style={{ marginBottom: 24 }}>
          <Row gutter={[12, 12]}>
            {visibleField([
              '10',
              '30',
              '41',
              '60',
              '70',
              '90',
              '201',
              '202',
              '203',
              '500',
              '900',
            ]) && (
              <Col xs={24} sm={24} md={24} lg={12}>
                <FloatingFormItem
                  name={['itemTaxes', 'icmsStAliquot']}
                  label="Alíquota"
                >
                  <InputDecimal fractions={4} max={100} />
                </FloatingFormItem>
              </Col>
            )}

            {visibleField([
              '00',
              '10',
              '30',
              '70',
              '90',
              '201',
              '202',
              '203',
              '900',
            ]) && (
              <Col xs={24} sm={24} md={24} lg={12}>
                <FloatingFormItem
                  name={['itemTaxes', 'icmsStDeterminationMode']}
                  label="Modalidade de determinação da BC do ICMS"
                >
                  <Select allowClear options={icmsStModalityOptions} />
                </FloatingFormItem>
              </Col>
            )}

            {visibleField([
              '00',
              '10',
              '30',
              '41',
              '60',
              '70',
              '90',
              '201',
              '202',
              '203',
              '500',
              '900',
            ]) && (
              <Col xs={24} sm={24} md={24} lg={12}>
                <FloatingFormItem
                  name={['itemTaxes', 'icmsStBaseCalculationValue']}
                  label="Valor da BC do ICMS ST"
                >
                  <InputDecimal />
                </FloatingFormItem>
              </Col>
            )}

            {visibleField(['10', '30', '90', '201', '202', '203', '900']) && (
              <Col xs={24} sm={24} md={24} lg={12}>
                <FloatingFormItem
                  name={[
                    'itemTaxes',
                    'icmsStBaseCalculationReductionPercentage',
                  ]}
                  label="Percentual da Redução de BC"
                >
                  <InputDecimal />
                </FloatingFormItem>
              </Col>
            )}

            {visibleField([
              '10',
              '30',
              '70',
              '90',
              '201',
              '202',
              '203',
              '900',
            ]) && (
              <Col xs={24} sm={24} md={24} lg={12}>
                <FloatingFormItem
                  name={['itemTaxes', 'icmsStValueAddedMarginPercentage']}
                  label="Valor adicionado (%)"
                >
                  <InputDecimal max={100} />
                </FloatingFormItem>
              </Col>
            )}

            {visibleField(['60']) && (
              <Col xs={24} sm={24} md={24} lg={12}>
                <FloatingFormItem
                  name={['itemTaxes', 'icmsDestinyUfBaseCalculationValue']}
                  label="Valor da BC do ICMS ST da UF destino"
                >
                  <InputDecimal />
                </FloatingFormItem>
              </Col>
            )}

            {visibleField(['60']) && (
              <Col xs={24} sm={24} md={24} lg={12}>
                <FloatingFormItem
                  name={['itemTaxes', 'icmsDestinyUfValue']}
                  label="Valor do ICMS ST da UF destino"
                >
                  <InputDecimal />
                </FloatingFormItem>
              </Col>
            )}

            {visibleField(['10', '60', '201', '202', '203', '500', '900']) && (
              <Col xs={24} sm={24} md={24} lg={12}>
                <FloatingFormItem
                  name={['itemTaxes', 'icmsStValue']}
                  label="Valor"
                >
                  <InputDecimal />
                </FloatingFormItem>
              </Col>
            )}
          </Row>
        </Card>
      )}

      {visibleField([
        '00',
        '10',
        '20',
        '30',
        '41',
        '51',
        '60',
        '70',
        '90',
        '201',
        '202',
        '203',
        '500',
        '900',
      ]) && (
        <Card title="ICMS ST FCP" type="inner" style={{ marginBottom: 24 }}>
          <Row gutter={[12, 12]}>
            {visibleField([
              '00',
              '10',
              '30',
              '41',
              '60',
              '70',
              '90',
              '201',
              '202',
              '203',
              '500',
              '900',
            ]) && (
              <Col xs={24} sm={24} md={24} lg={12}>
                <FloatingFormItem
                  name={['itemTaxes', 'icmsStFcpAliquot']}
                  label="Valor Alíquota FCP ST (%)"
                >
                  <InputDecimal fractions={4} max={100} />
                </FloatingFormItem>
              </Col>
            )}

            {visibleField([
              '00',
              '10',
              '20',
              '30',
              '41',
              '51',
              '60',
              '70',
              '90',
              '201',
              '202',
              '203',
              '500',
              '900',
            ]) && (
              <Col xs={24} sm={24} md={24} lg={12}>
                <FloatingFormItem
                  name={['itemTaxes', 'icmsStFcpBaseCalculationValue']}
                  label="Valor da Base de Cálculo do FCP retido por Substituição Tributária"
                >
                  <InputDecimal />
                </FloatingFormItem>
              </Col>
            )}

            {visibleField([
              '10',
              '60',
              '70',
              '90',
              '201',
              '202',
              '203',
              '500',
              '900',
            ]) && (
              <Col xs={24} sm={24} md={24} lg={12}>
                <FloatingFormItem
                  name={['itemTaxes', 'icmsStFcpValue']}
                  label="Valor do FCP retido por Substituição Tributária"
                >
                  <InputDecimal />
                </FloatingFormItem>
              </Col>
            )}
          </Row>
        </Card>
      )}

      {visibleField(['00', '10', '20', '51', '70', '90']) && (
        <Card title="ICMS FCP" type="inner" style={{ marginBottom: 24 }}>
          <Row gutter={[12, 12]}>
            <Col xs={24} sm={24} md={24} lg={12}>
              <FloatingFormItem
                name={['itemTaxes', 'icmsFcpAliquot']}
                label="Percentual relativo ao Fundo de Combate à Pobreza (Alíquota)"
              >
                <InputDecimal fractions={4} max={100} />
              </FloatingFormItem>
            </Col>

            {visibleField(['10', '20', '51', '70', '90']) && (
              <Col xs={24} sm={24} md={24} lg={12}>
                <FloatingFormItem
                  name={['itemTaxes', 'icmsFcpBaseCalculationValue']}
                  label="Valor da Base de Cálculo do FCP"
                >
                  <InputDecimal />
                </FloatingFormItem>
              </Col>
            )}
          </Row>
        </Card>
      )}

      {visibleField(['20', '30', '40', '41', '50', '70']) && (
        <Card
          title="ICMS Desonaração"
          type="inner"
          style={{ marginBottom: 24 }}
        >
          <Row gutter={[12, 12]}>
            <Col xs={24} sm={24} md={24} lg={12}>
              <FloatingFormItem
                name={['itemTaxes', 'icmsExemptionValue']}
                label="Valor da desoneração"
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>

            <Col xs={24} sm={24} md={24} lg={12}>
              <FloatingFormItem
                name={['itemTaxes', 'icmsExemptionReason']}
                label="Motivo da desoneração"
              >
                <Select options={icmsExemptionReasonOptions} />
              </FloatingFormItem>
            </Col>
          </Row>
        </Card>
      )}

      {visibleField(['51']) && (
        <Card
          title="ICMS Diferimento"
          type="inner"
          style={{ marginBottom: 24 }}
        >
          <Row gutter={[12, 12]}>
            <Col xs={24} sm={24} md={24} lg={12}>
              <FloatingFormItem
                name={['itemTaxes', 'icmsDefermentPercentage']}
                label="Percentual do diferimento"
              >
                <InputDecimal max={100} fractions={4} />
              </FloatingFormItem>
            </Col>

            <Col xs={24} sm={24} md={24} lg={12}>
              <FloatingFormItem
                name={['itemTaxes', 'icmsDefermentValue']}
                label="Valor do ICMS diferido"
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>

            <Col xs={24} sm={24} md={24} lg={12}>
              <FloatingFormItem
                name={['itemTaxes', 'icmsDefermentDueValue']}
                label="Valor do ICMS realmente devido"
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>
          </Row>
        </Card>
      )}

      {visibleField(['500']) && (
        <Card title="ICMS Efetivo" type="inner" style={{ marginBottom: 24 }}>
          <Row gutter={[12, 12]}>
            <Col xs={24} sm={24} md={24} lg={12}>
              <FloatingFormItem
                name={['itemTaxes', 'icmsEffectiveBaseCalculationValue']}
                label="Percentual de redução"
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>

            <Col xs={24} sm={24} md={24} lg={12}>
              <FloatingFormItem
                name={[
                  'itemTaxes',
                  'icmsEffectiveBaseCalculationReductionPercentage',
                ]}
                label="Percentual de redução"
              >
                <InputDecimal max={100} />
              </FloatingFormItem>
            </Col>

            <Col xs={24} sm={24} md={24} lg={12}>
              <FloatingFormItem
                name={['itemTaxes', 'icmsEffectiveAliquot']}
                label="Áliquota"
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>

            <Col xs={24} sm={24} md={24} lg={12}>
              <FloatingFormItem
                name={['itemTaxes', 'icmsEffectiveValue']}
                label="Valor"
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>
          </Row>
        </Card>
      )}
    </Form>
  );
}
